import React, { useState, useRef, useCallback } from 'react';
import { ProjectProvider, useProject } from './context/ProjectContext';
import Header from './components/Header';
import ProjectTree from './components/ProjectTree';
import DockableEditor from './components/DockableEditor';
import ProjectSettings from './components/ProjectSettings';
import ExportsView from './components/ExportsView';
import GeraeteView from './components/GeraeteView';
import StromlaufplanView from './components/StromlaufplanView';
import SettingsView from './components/SettingsView';
import KundenView from './components/KundenView';
import MitarbeiterView from './components/MitarbeiterView';
import ProjektUebersicht from './components/ProjektUebersicht';
import { PanelLeftClose, PanelLeft, GripVertical } from 'lucide-react';
import { Button } from './components/ui/button';
import { Toaster } from 'sonner';
import './App.css';

// Eigener Resizable Split Container (Horizontal)
const ResizableSplitH = ({ left, right, defaultLeftWidth = 35, minLeftWidth = 15, maxLeftWidth = 75, leftMinPx = 150 }) => {
  const [leftWidth, setLeftWidth] = useState(defaultLeftWidth);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging || !containerRef.current) return;
    
    const container = containerRef.current;
    const rect = container.getBoundingClientRect();
    const newWidth = ((e.clientX - rect.left) / rect.width) * 100;
    
    // Begrenzen auf min/max
    const clampedWidth = Math.max(minLeftWidth, Math.min(maxLeftWidth, newWidth));
    setLeftWidth(clampedWidth);
  }, [isDragging, minLeftWidth, maxLeftWidth]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  return (
    <div 
      ref={containerRef}
      className="flex flex-1 overflow-hidden h-full"
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      {/* Linkes Panel */}
      <div style={{ width: `${leftWidth}%`, minWidth: `${leftMinPx}px` }} className="flex-shrink-0 overflow-hidden h-full">
        {left}
      </div>
      
      {/* Resize Handle */}
      <div
        className={`w-2 flex-shrink-0 flex items-center justify-center cursor-col-resize transition-colors ${
          isDragging ? 'bg-emerald-500' : 'bg-gray-200 hover:bg-emerald-400'
        }`}
        onMouseDown={handleMouseDown}
      >
        <GripVertical className="w-3 h-3 text-gray-500" />
      </div>
      
      {/* Rechtes Panel */}
      <div className="flex-1 overflow-hidden h-full">
        {right}
      </div>
    </div>
  );
};

// Eigener Resizable Split Container (Vertikal)
const ResizableSplitV = ({ top, bottom, defaultTopHeight = 60, minTopHeight = 20, maxTopHeight = 85, topMinPx = 100 }) => {
  const [topHeight, setTopHeight] = useState(defaultTopHeight);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging || !containerRef.current) return;
    
    const container = containerRef.current;
    const rect = container.getBoundingClientRect();
    const newHeight = ((e.clientY - rect.top) / rect.height) * 100;
    
    // Begrenzen auf min/max
    const clampedHeight = Math.max(minTopHeight, Math.min(maxTopHeight, newHeight));
    setTopHeight(clampedHeight);
  }, [isDragging, minTopHeight, maxTopHeight]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  return (
    <div 
      ref={containerRef}
      className="flex flex-col flex-1 overflow-hidden w-full"
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      {/* Oberes Panel */}
      <div style={{ height: `${topHeight}%`, minHeight: `${topMinPx}px` }} className="flex-shrink-0 overflow-hidden w-full">
        {top}
      </div>
      
      {/* Resize Handle */}
      <div
        className={`h-2 flex-shrink-0 flex items-center justify-center cursor-row-resize transition-colors ${
          isDragging ? 'bg-emerald-500' : 'bg-gray-200 hover:bg-emerald-400'
        }`}
        onMouseDown={handleMouseDown}
      >
        <GripVertical className="w-3 h-3 text-gray-500 rotate-90" />
      </div>
      
      {/* Unteres Panel */}
      <div className="flex-1 overflow-hidden w-full">
        {bottom}
      </div>
    </div>
  );
};

// Alias für Abwärtskompatibilität
const ResizableSplit = ResizableSplitH;

// Hauptinhalt-Komponente (braucht Zugriff auf ProjectContext)
const AppContent = () => {
  const { currentProject } = useProject();
  const [activeTab, setActiveTab] = useState('planung');
  // Navigation: 'projekt' = Projekt-Editor, 'uebersicht' = Projektübersicht, 'kunden' = Kundenverwaltung, 'mitarbeiter' = Mitarbeiterverwaltung
  const [currentView, setCurrentView] = useState('projekt');
  // Planung-Ansicht: true = mit Planungspanel, false = nur Stromlaufplan
  const [showPlanningPanel, setShowPlanningPanel] = useState(true);
  // Editor-Dock Position: 'right' (neben Baum), 'bottom' (unter Baum), 'floating'
  const [editorDockPosition, setEditorDockPosition] = useState('right');


  const handleNavigateToOverview = (targetView) => {
    if (targetView) {
      setCurrentView(targetView);
    } else {
      setCurrentView('uebersicht');
    }
  };

  const handleSelectProject = () => {
    setCurrentView('projekt');
    setActiveTab('planung');
  };

  const renderMainContent = () => {
    // Globale Views (ohne Projekt)
    if (currentView === 'uebersicht') {
      return (
        <ProjektUebersicht 
          onSelectProject={handleSelectProject}
          onNavigateToKunden={() => setCurrentView('kunden')}
        />
      );
    }

    if (currentView === 'kunden') {
      return <KundenView onSelectBuilding={(customer, building) => {
        // TODO: Filter projects by building
        setCurrentView('uebersicht');
      }} />;
    }

    if (currentView === 'mitarbeiter') {
      return <MitarbeiterView />;
    }

    // Einstellungen und Geräte (global, nicht projekt-gebunden)
    if (activeTab === 'einstellungen') {
      return <SettingsView />;
    }

    if (activeTab === 'geraete') {
      return <GeraeteView />;
    }

    // Projekt-spezifische Views
    if (!currentProject) {
      return (
        <div className="flex-1 flex items-center justify-center bg-gray-50">
          <div className="text-center text-gray-400">
            <svg className="w-16 h-16 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
            </svg>
            <p className="text-lg">Kein Projekt ausgewählt</p>
            <p className="text-sm mt-2">Wählen Sie ein Projekt aus dem Menü &quot;Projekte&quot;</p>
          </div>
        </div>
      );
    }

    switch (activeTab) {
      case 'allgemein':
        // Split-View: Links Projekteinstellungen, rechts Stromlaufplan
        return (
          <ResizableSplit
            defaultLeftWidth={40}
            minLeftWidth={20}
            maxLeftWidth={75}
            left={<ProjectSettings />}
            right={<StromlaufplanView compact={true} />}
          />
        );
      case 'planung':
        // Split-View: Links Planung (Baum + Editor), rechts Stromlaufplan
        if (!showPlanningPanel) {
          return (
            <div className="flex flex-col flex-1">
              <div className="flex items-center px-2 py-1 bg-gray-100 border-b border-gray-200">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs"
                  onClick={() => setShowPlanningPanel(true)}
                  title="Planung einblenden"
                >
                  <PanelLeft className="w-3 h-3 mr-1" />
                  Planung einblenden
                </Button>
              </div>
              <StromlaufplanView compact={false} />
            </div>
          );
        }
        
        const leftPanel = (
          <div className="flex flex-col h-full border-r border-gray-300">
            {/* Toggle Button */}
            <div className="flex items-center justify-between px-2 py-1 bg-gray-100 border-b border-gray-200">
              <span className="text-xs font-medium text-gray-600">Planung</span>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs"
                onClick={() => setShowPlanningPanel(false)}
                title="Planung ausblenden"
              >
                <PanelLeftClose className="w-3 h-3" />
              </Button>
            </div>
            <div className="flex flex-1 overflow-hidden">
              {/* Layout abhängig von Editor-Position */}
              {editorDockPosition === 'right' ? (
                // Baum links, Editor rechts (nebeneinander) - mit Resize
                <ResizableSplitH
                  defaultLeftWidth={55}
                  minLeftWidth={30}
                  maxLeftWidth={80}
                  leftMinPx={150}
                  left={<ProjectTree />}
                  right={
                    <DockableEditor 
                      defaultDockPosition="right" 
                      onDockChange={setEditorDockPosition}
                    />
                  }
                />
              ) : editorDockPosition === 'bottom' ? (
                // Baum oben, Editor unten (übereinander) - mit Resize
                <ResizableSplitV
                  defaultTopHeight={60}
                  minTopHeight={25}
                  maxTopHeight={80}
                  topMinPx={100}
                  top={<ProjectTree />}
                  bottom={
                    <DockableEditor 
                      defaultDockPosition="bottom" 
                      onDockChange={setEditorDockPosition}
                    />
                  }
                />
              ) : (
                // Floating: nur Baum, Editor als separates Fenster
                <div className="w-full h-full overflow-hidden">
                  <ProjectTree />
                </div>
              )}
            </div>
          </div>
        );
        
        return (
          <div className="flex flex-1 overflow-hidden relative">
            <ResizableSplit
              defaultLeftWidth={35}
              minLeftWidth={15}
              maxLeftWidth={70}
              left={leftPanel}
              right={<StromlaufplanView compact={true} />}
            />
            
            {/* Floating Editor (wenn floating) */}
            {editorDockPosition === 'floating' && (
              <DockableEditor 
                defaultDockPosition="floating" 
                onDockChange={setEditorDockPosition}
              />
            )}
          </div>
        );
      case 'exporte':
        return <ExportsView />;
      case 'verteilerbau':
        return (
          <div className="flex items-center justify-center h-full bg-gray-50">
            <div className="text-center text-gray-400">
              <svg className="w-16 h-16 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
              </svg>
              <p className="text-lg">Aufbauplan</p>
              <p className="text-sm mt-2">Visuelle Verteiler-Darstellung (Prio 2)</p>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  // Bestimme ob Header Projekt-Tabs anzeigen soll
  const showProjectTabs = currentView === 'projekt' && currentProject;

  return (
    <div className="h-screen flex flex-col bg-gray-100">
      <Toaster position="top-right" richColors />
      <Header
        activeTab={activeTab}
        setActiveTab={(tab) => {
          setActiveTab(tab);
          // Bei Klick auf Einstellungen/Geräte zur Projekt-Ansicht wechseln
          if (tab === 'einstellungen' || tab === 'geraete') {
            setCurrentView('projekt');
          }
        }}
        onNavigateToOverview={handleNavigateToOverview}
      />
      <main className="flex-1 overflow-hidden flex">
        {renderMainContent()}
      </main>
    </div>
  );
};

function App() {
  return (
    <ProjectProvider>
      <AppContent />
    </ProjectProvider>
  );
}

export default App;
